// Paso 2 — TU PRODUCTO
//
// Tres tarjetas: ECONÓMICA / MEDIA / PREMIUM. Bajo cada una, una sugerencia de
// piezas calculada con `suggestPanels` (si el módulo existe).

import { state, update } from '/src/shared/state.js';
import { t } from '/src/shared/i18n.js';
import { ctaButton, setButtonDisabled } from '/src/ui/components/cta-button.js';

// Datos por si `core/catalog.js` aún no existe.
const FALLBACK_PRODUCTS = [
  {
    id: 'economica',
    tier: 'ECONÓMICA',
    name: 'C.610',
    pitch: 'Adhesivo al techo. Sencilla, eficaz, sin obra.',
    dim: '60 mm · 0,3721 m²/ud',
    price: 55.79,
    areaPerUnit: 0.3721,
    icon: '◻',
  },
  {
    id: 'media',
    tier: 'MEDIA',
    name: 'R.1220 × 305',
    pitch: 'Tejido ILUCS imantado. Alta absorción, montaje limpio.',
    dim: '60 mm · 0,3721 m²/ud',
    price: 137.21,
    areaPerUnit: 0.3721,
    icon: '◧',
  },
  {
    id: 'premium',
    tier: 'PREMIUM',
    name: 'WB 1,22 × 2 m',
    pitch: 'Panel grande colgado. Máximo rendimiento acústico.',
    dim: '33 mm · 2,44 m²/ud',
    price: 564.59,
    areaPerUnit: 2.44,
    icon: '▮',
  },
];

// Adaptador: el catálogo canónico (core/catalog.js) usa claves nombre/desc/precio/areaM2.
// Este step UI espera tier/name/pitch/dim/price/areaPerUnit/icon. Mapeamos sin romper nada.
const PITCHES = {
  ECONÓMICA: 'Adhesivo al techo. Sencilla, eficaz, sin obra.',
  MEDIA: 'Tejido ILUCS imantado. Alta absorción, montaje limpio.',
  PREMIUM: 'Panel grande colgado. Máximo rendimiento acústico.',
};
const ICONS = { ECONÓMICA: '◻', MEDIA: '◧', PREMIUM: '▮' };

function adaptProduct(p) {
  const tier = p.nombre || p.tier || '';
  const descParts = String(p.desc || '').split('·').map((s) => s.trim());
  return {
    ...p,
    id: p.id,
    tier,
    name: descParts[0] || p.formato || '',
    pitch: PITCHES[tier] || '',
    dim: [descParts[1], descParts[2]].filter(Boolean).join(' · ') || p.dimension || '',
    price: p.precio ?? p.price ?? 0,
    areaPerUnit: p.areaM2 ?? p.areaPerUnit ?? 0,
    icon: ICONS[tier] || '◻',
  };
}

async function loadProducts() {
  try {
    const mod = await import('/src/core/catalog.js');
    if (mod && Array.isArray(mod.PRODUCTS) && mod.PRODUCTS.length) {
      return mod.PRODUCTS.map(adaptProduct);
    }
  } catch (_err) {
    /* todavía no existe */
  }
  return FALLBACK_PRODUCTS;
}

async function loadSuggestPanels() {
  try {
    const mod = await import('/src/core/coverage.js');
    if (mod && typeof mod.suggestPanels === 'function') return mod.suggestPanels;
  } catch (_err) {
    /* todavía no existe */
  }
  return null;
}

function fallbackSuggest(room, product) {
  if (!room?.largo || !room?.ancho || !room?.altura || !product?.areaPerUnit) return 0;
  // Heurística sencilla: 30% de la superficie del techo.
  const ceil = room.largo * room.ancho;
  return Math.max(1, Math.ceil((ceil * 0.3) / product.areaPerUnit));
}

export function renderStep2(host, { next, back }) {
  host.innerHTML = `
    <header class="step__header">
      <h1 class="step__title" id="step-2-title">${t('step.2.title')}</h1>
      <p class="step__subtitle">${t('step.2.subtitle')}</p>
    </header>
    <div class="product-grid" id="product-grid"></div>
    <div class="sticky-cta" id="sticky-cta"></div>
  `;

  const grid = host.querySelector('#product-grid');
  const stickyHost = host.querySelector('#sticky-cta');

  const backBtn = ctaButton({
    label: t('nav.back'),
    variant: 'secondary',
    onClick: back,
  });
  const nextBtn = ctaButton({
    label: 'Calcular paneles',
    variant: 'primary',
    disabled: !state.product,
    onClick: next,
  });
  stickyHost.appendChild(backBtn);
  stickyHost.appendChild(nextBtn);

  Promise.all([loadProducts(), loadSuggestPanels()]).then(([products, suggestFn]) => {
    grid.innerHTML = '';
    for (const p of products) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'product-card';
      const selected = state.product?.id === p.id;
      card.setAttribute('aria-pressed', String(selected));

      const qty = suggestFn
        ? safeCall(suggestFn, { room: state.room, product: p }, 0)
        : fallbackSuggest(state.room, p);

      card.innerHTML = `
        <div class="product-card__icon" aria-hidden="true">${p.icon || '◻'}</div>
        <div class="product-card__name">${escapeHtml(p.tier)} · ${escapeHtml(p.name)}</div>
        <div class="product-card__meta">${escapeHtml(p.dim)} · ${formatPrice(p.price)} €/ud</div>
        <p class="product-card__pitch">${escapeHtml(p.pitch || '')}</p>
        <div class="product-card__suggest">≈ ${qty} piezas para tu sala</div>
      `;

      card.addEventListener('click', () => {
        update({ product: p });
        Array.from(grid.children).forEach((c) => c.setAttribute('aria-pressed', 'false'));
        card.setAttribute('aria-pressed', 'true');
        setButtonDisabled(nextBtn, false);
      });

      grid.appendChild(card);
    }
  });
}

function safeCall(fn, ...args) {
  try {
    return fn(...args);
  } catch {
    return 0;
  }
}
function formatPrice(n) {
  return Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
